import type { Theme } from '@mui/material';
import { Box, Typography } from '@mui/material';
import { useValuationsStore } from '@/pages/ValuationsV2/store/valuationsStore';
import { combineSx } from '@/utils/combineSx';
import { useTimelineEvents } from './useTimelineEvents';

const timelineStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  eventsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative',
    gap: '1rem',
  },
  trackBackground: (theme: Theme) => ({
    position: 'absolute',
    top: '4px',
    left: '0',
    right: '0',
    height: '1px',
    backgroundColor: theme.palette.divider,
    zIndex: 10,
    opacity: 0.5,
  }),
  eventContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    position: 'relative',
    zIndex: 1,
    flex: 1,
    minWidth: 0,
  },
  markerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  marker: (theme: Theme) => ({
    position: 'relative',
    zIndex: 20,
    width: '0.5rem',
    height: '0.5rem',
    borderRadius: '50%',
    flexShrink: 0,
    transition: 'backgroundColor 0.2s var(--ease-move)',
    '&[data-marker="active"]': {
      backgroundColor: theme.palette.baseColors.deepSeaNavy.c100,
    },
    '&[data-marker="inactive"]': {
      backgroundColor: theme.palette.divider,
      opacity: 0.4,
    },
  }),
  eventContent: (theme: Theme) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    width: '100%',
    paddingLeft: '1rem',
    '&[data-marker="inactive"]': {
      '&:before': {
        backgroundColor: theme.palette.divider,
      },
    },
    '&:before': {
      position: 'absolute',
      top: '0',
      left: '0',
      translate: 'calc(calc(0.5rem / 2) - 50%) -25% 0',
      content: '" "',
      height: '125%',
      width: '0.0625rem',
      backgroundColor: theme.palette.baseColors.deepSeaNavy.c100o15,
    },
  }),
  eventLabel: (theme: Theme) => ({
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    lineHeight: 1,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  }),
  eventDate: (theme: Theme) => ({
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  }),
};

type TimelineEventItemProps = {
  label: string;
  date: string | null;
};

const TimelineEventItem = ({ label, date }: TimelineEventItemProps) => {
  return (
    <Box sx={timelineStyles.eventContainer} data-id='timeline-event'>
      <Box sx={timelineStyles.markerRow} data-id='timeline-event-marker-row'>
        <Box
          sx={timelineStyles.marker}
          data-marker={date ? 'active' : 'inactive'}
          data-id='timeline-event-marker'
        />
      </Box>
      <Box
        sx={combineSx(timelineStyles.eventContent, { opacity: date ? 1 : 0.5 })}
        data-id='timeline-event-content'
        data-marker={date ? 'active' : 'inactive'}
      >
        <Typography sx={timelineStyles.eventLabel} data-id='timeline-event-label'>
          {label}
        </Typography>
        <Typography sx={timelineStyles.eventDate} data-id='timeline-event-date'>
          {date || '—'}
        </Typography>
      </Box>
    </Box>
  );
};

const TimelineView = () => {
  const selectedAssetDetails = useValuationsStore.use.selectedAssetDetails();
  const timelineEvents = useTimelineEvents(selectedAssetDetails);

  if (timelineEvents.length === 0) {
    return null;
  }

  return (
    <Box sx={timelineStyles.container} data-id='timeline-view'>
      <Box sx={timelineStyles.eventsWrapper} data-id='timeline-events-wrapper'>
        <Box sx={timelineStyles.trackBackground} data-id='timeline-line' />
        {timelineEvents.map(event => (
          <TimelineEventItem key={event.label} label={event.label} date={event.date} />
        ))}
      </Box>
    </Box>
  );
};

export { TimelineView };
